import React, {useState} from "react";
import {injectIntl} from "react-intl";
import {connect} from "react-redux";
import ModalWrapper from "../ModalWrapper";
import SingleTrip from "../../MyTrips/TripsContainer/SingleTrip";
import TripDetailsBody from "./TripDetailsBody";
import {RateDriverButtons} from "../RateDriver/RateDriverButtons";
import about from "../../../assets/images/about.png";

/** Ids that carry URLs or actions, not inline copy for trip details. */
const DISPLAY_TEXT_IDS_SKIP = new Set(['CRYPTO_PAYMENT_URL', 'TOLL_TIP_DISCLAIMER_URL']);

/**
 * Merge trip + fare `displayTextList` in API order, first occurrence wins per `id`.
 * Surfaces fare/charge copy that is not always keyed as ITINERARY_MESSAGE (e.g. FOP_TYPE_BASIC_DESC).
 */
function mergeTripDisplayTextNotes(tripList, fareList) {
    const merged = [
        ...(Array.isArray(tripList) ? tripList : []),
        ...(Array.isArray(fareList) ? fareList : []),
    ];
    const byId = new Map();
    for (const item of merged) {
        if (!item || !item.id) continue;
        const text = item.text != null ? String(item.text).trim() : '';
        if (!text) continue;
        if (DISPLAY_TEXT_IDS_SKIP.has(item.id)) continue;
        if (!byId.has(item.id)) byId.set(item.id, item);
    }
    return Array.from(byId.values());
}

const footnoteRowStyle = {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    padding: '14px 0',
    borderTop: '1px solid #e8e8e0',
    fontSize: '13px',
    lineHeight: 1.55,
    color: '#444',
};

// Mirrors the mobile app's Trip Details data: trip summary, fare + capacity, driver rating,
// price quote and itinerary message. Map and car/driver hero images are omitted on web.
const TripDetailsModal = ({modalType, trips}) => {
    const [closeModal] = useState(false);

    if (!trips || !trips.trip) {
        return null;
    }

    const {trip} = trips;
    const {driver, fare, displayTextList, tripDisplayStatus, tripOptions} = trip;

    const isCompleted = tripDisplayStatus === "Completed";

    // App pattern: "Your Trip Details with BRIZ" by default, or with the driver's name once the trip is completed.
    const bookedWithName = isCompleted && driver
        ? `${driver.firstName} ${driver.lastName}`.toUpperCase()
        : "BRIZ";
    const headerTitle = `Your Trip Details with ${bookedWithName}`;

    const priceQuoteText = fare && fare.priceQuotesIncludes != null
        ? String(fare.priceQuotesIncludes).trim()
        : '';
    const displayTextNotes = mergeTripDisplayTextNotes(displayTextList, fare && fare.displayTextList).filter(
        (note) => !priceQuoteText || String(note.text).trim() !== priceQuoteText
    );
    const showDriverRating = Array.isArray(tripOptions) && tripOptions.includes("DriverRating");
    const currentDriverRating = (trip.driverRating && trip.driverRating.ratingId) || 0;

    return (
        <ModalWrapper
            modalType={modalType}
            title={headerTitle}
            closeModalIndicator={closeModal}
            headerWithX={true}
            shrinkToContent={true}
        >
            <div
                className="trip-details-modal-body"
                style={{
                    maxHeight: 'calc(90vh - 100px)',
                    overflowY: 'auto',
                    padding: '12px 20px 24px',
                    boxSizing: 'border-box',
                }}
            >
                <div className='f-row' style={{paddingBottom: '12px'}}>
                    <SingleTrip trip={trip} hideTripOptions={true}/>
                </div>

                <TripDetailsBody trip={trip}/>

                {showDriverRating && (
                    <div className='f-row' style={{justifyContent: "center", padding: "16px 0 0"}}>
                        <RateDriverButtons displayOnly={currentDriverRating}/>
                    </div>
                )}

                <div
                    className="trip-details-footnotes"
                    style={{
                        marginTop: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                    }}
                >
                    {priceQuoteText && (
                        <div role="note" style={footnoteRowStyle}>
                            <img
                                src={about}
                                alt=""
                                width={20}
                                height={20}
                                style={{flexShrink: 0, marginTop: '2px', opacity: 0.85}}
                            />
                            <span style={{textAlign: 'left'}}>{fare.priceQuotesIncludes}</span>
                        </div>
                    )}

                    {displayTextNotes.map((note) => (
                        <div key={note.id} role="note" style={footnoteRowStyle}>
                            <img
                                src={about}
                                alt=""
                                width={20}
                                height={20}
                                style={{flexShrink: 0, marginTop: '2px', opacity: 0.85}}
                            />
                            <span style={{textAlign: 'left'}}>{note.text}</span>
                        </div>
                    ))}
                </div>
            </div>
        </ModalWrapper>
    );
};

function mapStateToProps({user}) {
    const {trips} = user.lists;
    return {
        trips,
    };
}

export default injectIntl(connect(mapStateToProps)(TripDetailsModal));
